/**
 * Autocomplete-выбор компаний-доноров для фильтра поиска кандидатов
 * (TASKNEIROKLYUCH-697).
 *
 * Пользователь вводит подстроку → дёргает /applicants/donors/suggest →
 * выпадающий список реальных значений `last_job_org_name` с count кандидатов.
 * Выбор пункта добавляет точную строку в `value`. После этого
 * backend-ILIKE по такой точной строке = de-facto exact match — не цепляет
 * лишнего (Газпромбанк, Газпром-Медиа, WMT Group_Газпром).
 *
 * Совместим со старым контрактом: значение остаётся `string[]`, парент
 * не меняется. Ручной ввод (Enter без выбора из dropdown) тоже работает
 * — для backward compat с прежним поведением.
 */

import { XMarkIcon as XMarkIconSolid } from '@heroicons/react/24/solid';
import React, { useCallback, useEffect, useId, useRef, useState } from 'react';
import {
  DonorCompanySuggestion,
  suggestDonorCompanies,
} from '../services/api';

const MIN_QUERY_LENGTH = 2;
const DEBOUNCE_MS = 300;
const SUGGEST_LIMIT = 20;

interface Props {
  value: string[];
  onChange: (companies: string[]) => void;
  source: string;
  primaryColor: string;
  placeholder?: string;
}

const DonorCompaniesAutocomplete: React.FC<Props> = ({
  value,
  onChange,
  source,
  primaryColor,
  placeholder,
}) => {
  const [inputValue, setInputValue] = useState('');
  const [suggestions, setSuggestions] = useState<DonorCompanySuggestion[]>([]);
  const [isOpen, setIsOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [highlightedIdx, setHighlightedIdx] = useState(0);

  // Стабильные id для ARIA — связывают input(combobox) с listbox и option'ами.
  const reactId = useId();
  const listboxId = `${reactId}-listbox`;
  const optionId = (idx: number) => `${reactId}-option-${idx}`;

  const containerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const abortRef = useRef<AbortController | null>(null);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const addCompany = useCallback(
    (company: string) => {
      const trimmed = company.trim();
      if (!trimmed) return;
      if (value.includes(trimmed)) {
        setInputValue('');
        return;
      }
      onChange([...value, trimmed]);
      setInputValue('');
      setSuggestions([]);
      setIsOpen(false);
      setHighlightedIdx(0);
    },
    [value, onChange],
  );

  const removeCompany = useCallback(
    (company: string) => {
      onChange(value.filter((c) => c !== company));
    },
    [value, onChange],
  );

  // Debounced suggestion fetch
  useEffect(() => {
    const q = inputValue.trim();
    if (q.length < MIN_QUERY_LENGTH) {
      setSuggestions([]);
      setIsLoading(false);
      if (abortRef.current) {
        abortRef.current.abort();
        abortRef.current = null;
      }
      return;
    }

    const controller = new AbortController();
    abortRef.current = controller;

    debounceRef.current = setTimeout(async () => {
      setIsLoading(true);
      try {
        const res = await suggestDonorCompanies(source, q, SUGGEST_LIMIT, controller.signal);
        if (!controller.signal.aborted) {
          setSuggestions(res.items || []);
          setIsOpen(true);
          setHighlightedIdx(0);
        }
      } catch (err: unknown) {
        // AbortError при быстрой печати / unmount — нормально, не сбрасываем UI
        const isAbort = (err as { name?: string })?.name === 'AbortError';
        if (!isAbort && !controller.signal.aborted) {
          setSuggestions([]);
        }
      } finally {
        if (!controller.signal.aborted) setIsLoading(false);
      }
    }, DEBOUNCE_MS);

    // Cleanup отменяет таймер И запрос — защита от stale state и от
    // setState на unmounted компоненте.
    return () => {
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
        debounceRef.current = null;
      }
      controller.abort();
    };
  }, [inputValue, source]);

  // Закрытие dropdown по клику снаружи
  useEffect(() => {
    const onClickOutside = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', onClickOutside);
    return () => document.removeEventListener('mousedown', onClickOutside);
  }, []);

  const handleKeyDown: React.KeyboardEventHandler<HTMLInputElement> = (e) => {
    // Backspace при пустом input → удалить последний chip
    if (e.key === 'Backspace' && inputValue === '' && value.length > 0) {
      onChange(value.slice(0, -1));
      return;
    }
    if (!isOpen || suggestions.length === 0) {
      if (e.key === 'Enter') {
        e.preventDefault();
        addCompany(inputValue);
      }
      return;
    }
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setHighlightedIdx((i) => Math.min(i + 1, suggestions.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setHighlightedIdx((i) => Math.max(i - 1, 0));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      const picked = suggestions[highlightedIdx];
      if (picked) addCompany(picked.value);
      else addCompany(inputValue);
    } else if (e.key === 'Escape') {
      setIsOpen(false);
    }
  };

  return (
    <div ref={containerRef} className="relative w-full">
      <div className="bg-white relative rounded-[10px] w-full">
        <div className="box-border content-stretch flex gap-[8px] items-center overflow-clip pl-[12px] pr-[10px] py-[10px] relative w-full">
          <div className="basis-0 font-['Inter:Regular',_sans-serif] font-normal grow leading-[0] min-h-px min-w-px not-italic relative shrink-0 text-[14px] text-left tracking-[-0.084px]">
            <div className="flex flex-wrap gap-2 items-center min-h-[20px]">
              {value.map((company) => (
                <span
                  key={company}
                  className="inline-flex items-center rounded-full text-[12px] px-2 py-1 whitespace-nowrap"
                  style={{ backgroundColor: primaryColor + '20', color: primaryColor }}
                >
                  {company}
                  <button
                    type="button"
                    onClick={() => removeCompany(company)}
                    aria-label={`Удалить компанию ${company}`}
                    className="ml-1 rounded-full p-0.5"
                    style={{ color: primaryColor }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.backgroundColor = primaryColor + '40';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.backgroundColor = 'transparent';
                    }}
                  >
                    <XMarkIconSolid className="h-3 w-3" aria-hidden="true" />
                  </button>
                </span>
              ))}
              <input
                ref={inputRef}
                type="text"
                role="combobox"
                aria-expanded={isOpen && suggestions.length > 0}
                aria-controls={listboxId}
                aria-autocomplete="list"
                aria-activedescendant={
                  isOpen && suggestions.length > 0 ? optionId(highlightedIdx) : undefined
                }
                value={inputValue}
                placeholder={value.length === 0 ? placeholder ?? 'Начните вводить название' : ''}
                className="flex-1 min-w-0 outline-none bg-transparent text-[14px] leading-[20px] text-[#525866] placeholder:text-[#868c98]"
                onChange={(e) => setInputValue(e.currentTarget.value)}
                onFocus={() => {
                  if (suggestions.length > 0) setIsOpen(true);
                }}
                onKeyDown={handleKeyDown}
              />
            </div>
          </div>
        </div>
        <div
          aria-hidden="true"
          className="absolute border border-[#e2e4e9] border-solid inset-0 pointer-events-none rounded-[10px] shadow-[0px_1px_2px_0px_rgba(228,229,231,0.24)]"
        />
      </div>

      {isOpen && (isLoading || suggestions.length > 0) && (
        <div
          id={listboxId}
          role="listbox"
          className="absolute z-20 w-full mt-1 bg-white border border-[#e2e4e9] rounded-[10px] shadow-lg max-h-60 overflow-auto"
        >
          {isLoading && suggestions.length === 0 ? (
            <div className="px-4 py-2 text-[12px] text-[#868c98]">Загрузка…</div>
          ) : (
            suggestions.map((item, idx) => (
              <button
                key={item.value}
                id={optionId(idx)}
                type="button"
                role="option"
                aria-selected={idx === highlightedIdx}
                onMouseDown={(e) => {
                  // mousedown вместо click — успевает до blur'а input
                  e.preventDefault();
                  addCompany(item.value);
                }}
                onMouseEnter={() => setHighlightedIdx(idx)}
                className={`w-full px-4 py-2 text-left text-[14px] flex justify-between items-center ${
                  idx === highlightedIdx ? 'bg-gray-100' : 'hover:bg-gray-50'
                }`}
              >
                <span className="truncate">{item.value}</span>
                <span className="text-[12px] text-[#868c98] ml-2 shrink-0">{item.count}</span>
              </button>
            ))
          )}
        </div>
      )}
    </div>
  );
};

export default DonorCompaniesAutocomplete;
